import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AccessDenied: React.FC = () => {
  const navigate = useNavigate();
  const { refreshPermissions } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshPermissions = async () => {
    setIsRefreshing(true);
    try {
      await refreshPermissions();
      // Tentar voltar para a página anterior (que agora pode estar acessível)
      navigate(-1);
    } catch (error) {
      console.error('Erro ao atualizar permissões:', error);
      setIsRefreshing(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.iconWrapper}>
          <div style={styles.icon}>🔒</div>
        </div>
        <h1 style={styles.title}>Acesso Negado</h1>
        <p style={styles.message}>
          Você não tem permissão para acessar esta página.
        </p>
        <p style={styles.submessage}>
          Entre em contato com o administrador do sistema se você acredita que deveria ter acesso a este recurso.
        </p>
        <div style={styles.actions}>
          <button
            style={{
              ...styles.button,
              ...styles.btnBack,
              ...(isRefreshing ? styles.buttonDisabled : {})
            }}
            onClick={() => navigate(-1)}
            disabled={isRefreshing}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => !isRefreshing && Object.assign(e.currentTarget.style, styles.btnBackHover)}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => !isRefreshing && Object.assign(e.currentTarget.style, styles.btnBack)}
          >
            ← Voltar
          </button>
          <button
            style={{
              ...styles.button,
              ...styles.btnRefresh,
              ...(isRefreshing ? styles.buttonDisabled : {})
            }}
            onClick={handleRefreshPermissions}
            disabled={isRefreshing}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => !isRefreshing && Object.assign(e.currentTarget.style, styles.btnRefreshHover)}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => !isRefreshing && Object.assign(e.currentTarget.style, styles.btnRefresh)}
          >
            {isRefreshing ? '⏳ Atualizando...' : '🔄 Atualizar Permissões'}
          </button>
          <button
            style={{
              ...styles.button,
              ...styles.btnDashboard,
              ...(isRefreshing ? styles.buttonDisabled : {})
            }}
            onClick={() => navigate('/dashboard')}
            disabled={isRefreshing}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => !isRefreshing && Object.assign(e.currentTarget.style, styles.btnDashboardHover)}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => !isRefreshing && Object.assign(e.currentTarget.style, styles.btnDashboard)}
          >
            🏠 Ir para o Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'var(--gradient-primary)',
    padding: 'var(--spacing-xl)',
  },
  content: {
    background: 'var(--bg-white)',
    borderRadius: 'var(--radius-card)',
    padding: 'var(--spacing-5xl) var(--spacing-4xl)',
    maxWidth: '600px',
    width: '100%',
    boxShadow: 'var(--shadow-xl)',
    textAlign: 'center',
    animation: 'fadeIn var(--duration-slow) var(--easing-out)',
  },
  iconWrapper: {
    marginBottom: 'var(--spacing-3xl)',
  },
  icon: {
    fontSize: '80px',
    display: 'inline-block',
    animation: 'bounce 2s infinite',
    filter: 'grayscale(0%) brightness(1)',
  },
  title: {
    fontSize: 'var(--font-size-6xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--text-primary)',
    marginBottom: 'var(--spacing-xl)',
    marginTop: 0,
  },
  message: {
    fontSize: 'var(--font-size-lg)',
    color: 'var(--text-secondary)',
    marginBottom: 'var(--spacing-md)',
    lineHeight: 'var(--line-height-relaxed)',
  },
  submessage: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--text-muted)',
    marginBottom: 'var(--spacing-4xl)',
    lineHeight: 'var(--line-height-normal)',
  },
  actions: {
    display: 'flex',
    gap: 'var(--spacing-lg)',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  button: {
    padding: 'var(--button-padding)',
    border: 'none',
    borderRadius: 'var(--radius-button)',
    fontSize: 'var(--font-size-md)',
    fontWeight: 'var(--font-weight-semibold)',
    cursor: 'pointer',
    transition: 'var(--transition-default)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--button-gap)',
    fontFamily: 'var(--font-primary)',
  },
  btnBack: {
    background: 'var(--btn-cancel)',
    color: 'var(--text-white)',
  },
  btnBackHover: {
    background: 'var(--btn-cancel-hover)',
    transform: 'translateY(-2px)',
    boxShadow: 'var(--shadow-md)',
  },
  btnRefresh: {
    background: 'var(--color-warning)',
    color: 'var(--text-white)',
  },
  btnRefreshHover: {
    background: 'var(--btn-secondary)',
    transform: 'translateY(-2px)',
    boxShadow: 'var(--shadow-secondary)',
  },
  btnDashboard: {
    background: 'var(--btn-primary)',
    color: 'var(--text-white)',
  },
  btnDashboardHover: {
    background: 'var(--btn-primary-hover)',
    transform: 'translateY(-2px)',
    boxShadow: 'var(--shadow-primary)',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none',
  },
};

// Media query para responsividade
const mediaQueryList = window.matchMedia('(max-width: 768px)');
if (mediaQueryList.matches) {
  styles.content = {
    ...styles.content,
    padding: 'var(--spacing-4xl) var(--spacing-3xl)',
  };
  styles.title = {
    ...styles.title,
    fontSize: 'var(--font-size-4xl)',
  };
  styles.message = {
    ...styles.message,
    fontSize: 'var(--font-size-md)',
  };
  styles.actions = {
    ...styles.actions,
    flexDirection: 'column',
  };
  styles.button = {
    ...styles.button,
    width: '100%',
    justifyContent: 'center',
  };
}

export default AccessDenied;
